/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import type { EnterpriseFilterOptions_EnterpriseUserFilterLabel } from '__generated__/graphql-types';
import { EnterpriseFilterOptions_EntityType } from '__generated__/graphql-types';
import { debounce } from 'lodash';
import { compose } from 'recompose';

import getModernContext from 'js/lib/getModernContext';

import { Button, Divider, Grid, TemporaryDrawer, TextField, Typography2, useTheme } from '@coursera/cds-core';

import TabbedFilterOptionList from 'bundles/enterprise-admin-drawers/components/components/TabbedFilterOptionList';
import type { TabOption } from 'bundles/enterprise-admin-drawers/components/components/TabbedFilterOptionList';
import type { FilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import type { EnterpriseAdminPermissionsContextProps } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { EnterpriseAdminPermissionsContext } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import SearchFilterOptions from 'bundles/enterprise-admin-user-filters/queries/SearchFilterOptions.graphql';
import SearchFilterOptionsProgramLevel from 'bundles/enterprise-admin-user-filters/queries/SearchFilterOptionsProgramLevel.graphql';
import type {
  SearchFilterOptionsQuery,
  SearchFilterOptionsQueryVariables,
} from 'bundles/enterprise-admin-user-filters/queries/__generated__/SearchFilterOptions';
import type {
  SearchFilterOptionsProgramLevelQuery,
  SearchFilterOptionsProgramLevelQueryVariables,
} from 'bundles/enterprise-admin-user-filters/queries/__generated__/SearchFilterOptionsProgramLevel';
import {
  toEnterpriseFilterOptionsFilterType,
  toLegacyFilterOptionId,
  toLegacyFilterOptionType,
} from 'bundles/enterprise-admin-user-filters/queries/searchFilterOptionsUtils';
import { Spacer } from 'bundles/enterprise-ui';
import filterExistsOrDefault from 'bundles/program-common/utils/filterExistsOrDefault';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const { HeadingGroup, Content, Actions } = TemporaryDrawer;

const SelectionDrawer: React.VFC<{
  heading: string;
  headingSupportText?: string;
  searchLabel?: string;
  helpMessage: string;
  listLabel?: string;
  filterType: string;
  isOpen: boolean;
  selectedOptions: FilterOption[];
  availableOptions: FilterOption[];
  loading: boolean;
  defaultTab?: TabOption;
  onClose: () => void;
  onApply: (options: FilterOption[]) => void;
  onRefetch: (query: string) => void;
}> = ({
  heading,
  headingSupportText,
  searchLabel,
  helpMessage,
  listLabel,
  filterType,
  isOpen,
  selectedOptions,
  availableOptions,
  loading,
  defaultTab,
  onClose,
  onApply,
  onRefetch,
  ...props
}) => {
  const theme = useTheme();
  const [selectedFilterOptions, setSelectedFilterOptions] = React.useState<FilterOption[]>([]);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const firstRenderRef = React.useRef(true);

  // TODO: React Hook useCallback received a function whose dependencies are unknown. Pass an inline function instead.
  // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refetchDebounced = React.useCallback(debounce(onRefetch, 500), []);

  React.useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
    } else {
      refetchDebounced(searchQuery);
    }
  }, [searchQuery, refetchDebounced]);

  React.useEffect(() => {
    setSearchQuery('');
    if (selectedOptions) {
      setSelectedFilterOptions(selectedOptions);
    }
  }, [isOpen, selectedOptions]);

  const handleOnChange = (filterOption: FilterOption, checked: boolean) => {
    if (checked) {
      setSelectedFilterOptions((prevState) => [...prevState, filterOption]);
    } else {
      setSelectedFilterOptions((prevState) => [...prevState.filter((fo) => fo.id !== filterOption.id)]);
    }
  };

  const handleOnApply = () => {
    onApply(selectedFilterOptions);
    onClose();
  };

  const handleOnClose = () => {
    setSelectedFilterOptions([]);
    onClose();
  };

  return (
    <TemporaryDrawer open={isOpen} onClose={handleOnClose} width="medium" anchor="right" sticky="actions" {...props}>
      <HeadingGroup supportText={headingSupportText}>{heading}</HeadingGroup>
      <Content>
        {searchLabel && (
          <TextField
            fullWidth
            label={searchLabel}
            placeholder={searchLabel}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            data-testid="selection-drawer-search"
          />
        )}
        <TabbedFilterOptionList
          css={{ width: '100%' }}
          filterOptions={availableOptions}
          filterType={filterType}
          loading={loading}
          selectedFilterOptions={selectedFilterOptions}
          onChange={handleOnChange}
          preSorted={true}
          defaultTab={defaultTab}
          optionListHeader={
            <Grid container direction="column">
              <Typography2 component="h3" variant="subtitleMedium">
                {_t('#{numOfOptions} search results', { numOfOptions: availableOptions.length })}
              </Typography2>
              <Spacer size={12} />
              <Typography2 component="h3" variant="subtitleMedium">
                {helpMessage}
              </Typography2>
              <Spacer size={8} />
              <Divider orientation="horizontal" />
              <Spacer size={4} />
            </Grid>
          }
          listLabel={listLabel}
        />
      </Content>
      <Actions
        css={css`
          display: flex;
          gap: ${theme.spacing(16)};
          ${theme.breakpoints.down('xs')} {
            flex-direction: column;
          }
        `}
      >
        <Button
          css={css`
            ${theme.breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          variant="primary"
          onClick={handleOnApply}
          data-testid="selection-drawer-apply"
        >
          {_t('Apply selection')}
        </Button>
        <Button
          css={css`
            ${theme.breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          variant="secondary"
          onClick={handleOnClose}
          data-testid="selection-drawer-cancel"
        >
          {_t('Cancel')}
        </Button>
      </Actions>
    </TemporaryDrawer>
  );
};

export default SelectionDrawer;

type FilterType = 'PROGRAMS' | 'ENROLLMENTS';

export type PropsToWithUserFilterOptions = {
  thirdPartyOrganization: { id: string; slug: string };
  filterType: FilterType;
  onlySpecialization?: boolean;
};

export type PropsFromWithUserFilterOptions = {
  loading: boolean;
  filterOptions: FilterOption[];
  onRefetch: (query: string) => void;
};

const getFilterOptions = (
  elements: EnterpriseFilterOptions_EnterpriseUserFilterLabel[],
  onlySpecialization?: boolean
) => {
  const filteredElements = onlySpecialization
    ? elements.filter((e) => e.entityType === EnterpriseFilterOptions_EntityType.FilterOptionEntityTypeSpecialization)
    : elements;
  return filteredElements.map((e) => {
    const filterOptionType = toLegacyFilterOptionType(e);
    const id = toLegacyFilterOptionId(e);
    return { ...e, type: filterOptionType, id };
  });
};

export const onRefetchFilteringQuery =
  (organizationId: string, filterType: string, refetch?: (variables: SearchFilterOptionsQueryVariables) => void) =>
  (query: string) =>
    refetch?.({
      organizationId,
      filterType: toEnterpriseFilterOptionsFilterType(filterType),
      searchWord: query,
    });

export const onRefetchFilteringQueryForProgramAdmins =
  (
    programIds: string[],
    filterType: string,
    refetch?: (variables: SearchFilterOptionsProgramLevelQueryVariables) => void
  ) =>
  (query: string) =>
    refetch?.({
      programIds,
      filterType: toEnterpriseFilterOptionsFilterType(filterType),
      searchWord: query,
    });

type PropsFromEnterprisePermissionsContext = {
  enterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps;
};
export const withUserFilterOptions = compose<PropsFromWithUserFilterOptions, PropsToWithUserFilterOptions>(
  getModernContext('enterpriseAdminPermissionsContext', EnterpriseAdminPermissionsContext),
  graphql<
    PropsToWithUserFilterOptions & PropsFromEnterprisePermissionsContext,
    SearchFilterOptionsQuery,
    SearchFilterOptionsQueryVariables,
    PropsFromWithUserFilterOptions
  >(SearchFilterOptions, {
    options: ({ thirdPartyOrganization, filterType }) => ({
      variables: {
        organizationId: thirdPartyOrganization.id,
        filterType: toEnterpriseFilterOptionsFilterType(filterType),
        searchWord: '',
      },
      context: {
        clientName: 'gatewayGql',
      },
    }),
    skip: ({ enterpriseAdminPermissionsContext }) => !enterpriseAdminPermissionsContext.getIsOrganizationDomainAdmin(),
    props: ({ data, ownProps: { thirdPartyOrganization, onlySpecialization, filterType } }) => {
      const elements = filterExistsOrDefault(
        data?.EnterpriseFilterOptions?.searchFilterOptions?.edges?.map((e) => e?.node)
      );
      const loading = !!data?.loading;
      const filterOptions = getFilterOptions(elements, onlySpecialization);
      return {
        loading,
        onRefetch: onRefetchFilteringQuery(thirdPartyOrganization.id, filterType, data?.refetch),
        filterOptions,
      };
    },
  }),
  graphql<
    PropsToWithUserFilterOptions & PropsFromEnterprisePermissionsContext,
    SearchFilterOptionsProgramLevelQuery,
    SearchFilterOptionsProgramLevelQueryVariables,
    PropsFromWithUserFilterOptions
  >(SearchFilterOptionsProgramLevel, {
    options: ({ filterType, enterpriseAdminPermissionsContext }) => ({
      variables: {
        programIds: enterpriseAdminPermissionsContext.getAllProgramIdsWithPermission('DEFAULT_SUBDOMAIN', 'VIEW'),
        filterType: toEnterpriseFilterOptionsFilterType(filterType),
        searchWord: '',
      },
      context: {
        clientName: 'gatewayGql',
      },
    }),
    skip: ({ enterpriseAdminPermissionsContext }) => enterpriseAdminPermissionsContext.getIsOrganizationDomainAdmin(),
    props: ({ data, ownProps: { filterType, onlySpecialization, enterpriseAdminPermissionsContext } }) => {
      const elements = filterExistsOrDefault(
        data?.EnterpriseFilterOptions?.searchFilterOptionsProgramLevel?.edges?.map((e) => e?.node)
      );
      const loading = !!data?.loading;
      const filterOptions = getFilterOptions(elements, onlySpecialization);
      return {
        loading,
        onRefetch: onRefetchFilteringQueryForProgramAdmins(
          enterpriseAdminPermissionsContext.getAllProgramIdsWithPermission('DEFAULT_SUBDOMAIN', 'VIEW'),
          filterType,
          data?.refetch
        ),
        filterOptions,
      };
    },
  })
);
